import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'
import { Float } from 'type-graphql'
import { KpiPeriodType } from '../kpi/kpi'

import { KpiMetricValue } from './kpi-metric-value'
import { NewKpiMetricValue, KpiMetricValuePatch } from './kpi-metric-value-type'
import { KpiMetric } from '../kpi-metric/kpi-metric'
import { ScalarObject } from '@things-factory/shell'
import { getDefaultValueDate } from '../utils/value-date-util'

@Resolver(KpiMetricValue)
export class KpiMetricValueMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiMetricValue, { description: 'Create a new metric value with the provided details.' })
  async createKpiMetricValue(
    @Arg('metricValue', { description: 'Input object containing details for the new metric value.' })
    metricValue: NewKpiMetricValue,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValue> {
    const { domain, user, tx } = context.state

    let metric = metricValue.metricId
      ? await getRepository(KpiMetric).findOne({ where: { id: metricValue.metricId } })
      : undefined

    // valueDate 자동 생성/보정 로직 추가
    let periodType = metricValue.periodType
    if (!periodType && metric) {
      periodType = metric.periodType
    }
    if (!periodType) {
      periodType = KpiPeriodType.DAY
    }
    // periodType을 string으로 변환 후 KpiPeriodType으로 강제 변환
    periodType = KpiPeriodType[String(periodType) as keyof typeof KpiPeriodType]
    let valueDate = metricValue.valueDate
    if (!valueDate || !isValueDateValidForPeriodType(valueDate, periodType)) {
      valueDate = getDefaultValueDate(periodType)
    }

    const entity: Partial<KpiMetricValue> = {
      metric,
      metricId: metricValue.metricId,
      valueDate,
      periodType,
      value: metricValue.value,
      org: metricValue.org,
      unit: metricValue.unit,
      meta: metricValue.meta,
      domain: domain,
      creator: user,
      updater: user
    }

    return await getRepository(KpiMetricValue, tx).save(entity)
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiMetricValue, { description: 'Record a metric value by metric name, value, meta, and org.' })
  async recordKpiMetricValue(
    @Arg('metricName', { description: 'Metric code/name.' }) metricName: string,
    @Arg('value', type => Float, { nullable: true, description: 'Metric value (number).' }) value: number | null,
    @Arg('meta', type => ScalarObject, { nullable: true, description: 'Extended or non-numeric information (JSON).' })
    meta: any,
    @Arg('org', { nullable: true, description: 'Organizational unit for this value (project, department, company, employee, etc.)' })
    org: string | null,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValue> {
    const { domain, user, tx } = context.state
    const metric = await getRepository(KpiMetric).findOne({ where: { name: metricName, domain: { id: domain.id } } })
    if (!metric) throw new Error(`Metric not found: ${metricName}`)
    const periodType = metric.periodType || KpiPeriodType.DAY
    const valueDate = getDefaultValueDate(periodType)
    if (value == null && meta == null) {
      throw new Error('Either value or meta must be provided.')
    }
    const repo = getRepository(KpiMetricValue, tx)
    let entity = await repo.findOne({
      where: {
        metric: { id: metric.id },
        valueDate,
        periodType,
        org,
        domain: { id: domain.id }
      }
    })
    if (entity) {
      entity.value = value
      entity.meta = meta
      entity.updater = user
    } else {
      entity = repo.create({
        metric,
        metricId: metric.id,
        value,
        meta,
        valueDate,
        periodType,
        org,
        domain,
        creator: user,
        updater: user
      })
    }
    return await repo.save(entity)
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiMetricValue, { description: 'To modify KpiMetricValue information' })
  async updateKpiMetricValue(
    @Arg('id') id: string,
    @Arg('patch') patch: KpiMetricValuePatch,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValue> {
    const { domain, user, tx } = context.state

    const repository = getRepository(KpiMetricValue, tx)
    const metricValue = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    let metric = patch.metricId
      ? await getRepository(KpiMetric).findOne({ where: { id: patch.metricId } })
      : metricValue.metric

    const entity: any = {
      ...metricValue,
      ...patch,
      metric,
      updater: user
    }

    return await repository.save(entity)
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiMetricValue], { description: "To modify multiple KpiMetricValues' information" })
  async updateMultipleKpiMetricValue(
    @Arg('patches', type => [KpiMetricValuePatch]) patches: KpiMetricValuePatch[],
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValue[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag && patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag && patch.cuFlag.toUpperCase() === 'M')
    const metricValueRepo = getRepository(KpiMetricValue, tx)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        if (!newRecord.metricId) {
          throw new Error('metricId is required for creating new metric value')
        }

        const metric = await getRepository(KpiMetric).findOne({
          where: { domain: { id: domain.id }, id: newRecord.metricId }
        })

        if (!metric) {
          throw new Error(`Metric not found: ${newRecord.metricId}`)
        }

        const result = await metricValueRepo.save({
          ...newRecord,
          metric,
          domain,
          creator: user,
          updater: user
        } as any)
        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const metricValue = await metricValueRepo.findOne({
          where: { domain: { id: domain.id }, id: updateRecord.id }
        })

        if (!metricValue) {
          throw new Error(`failed to find metricValue to update: ${updateRecord.id}`)
        }

        if (!updateRecord.metricId) {
          throw new Error('metricId is required for updating value')
        }

        const metric = await getRepository(KpiMetric).findOne({
          where: { domain: { id: domain.id }, id: updateRecord.metricId }
        })

        if (!metric) {
          throw new Error(`Metric not found: ${updateRecord.metricId}`)
        }

        const result = await metricValueRepo.save({
          ...metricValue,
          ...updateRecord,
          metric,
          updater: user
        } as any)
        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete KpiMetricValue' })
  async deleteKpiMetricValue(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state
    await getRepository(KpiMetricValue, tx).delete({ domain: { id: domain.id }, id })
    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete multiple KpiMetricValues' })
  async deleteKpiMetricValues(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state
    await getRepository(KpiMetricValue, tx).delete({
      domain: { id: domain.id },
      id: In(ids)
    })
    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To import multiple KpiMetricValues' })
  async importKpiMetricValues(
    @Arg('metricValues', type => [KpiMetricValuePatch]) metricValues: KpiMetricValuePatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state
    await Promise.all(
      metricValues.map(async (metricValue: KpiMetricValuePatch) => {
        await getRepository(KpiMetricValue, tx).save({ domain, ...metricValue } as any)
      })
    )
    return true
  }
}

// valueDate와 periodType의 일치 여부를 검사하는 유틸리티 함수(임시, 아래에 추가)
function isValueDateValidForPeriodType(valueDate: string, periodType: KpiPeriodType): boolean {
  if (!valueDate) return false
  switch (periodType) {
    case KpiPeriodType.DAY:
      return /^\d{4}-\d{2}-\d{2}$/.test(valueDate)
    case KpiPeriodType.MONTH:
      return /^\d{4}-\d{2}$/.test(valueDate)
    case KpiPeriodType.QUARTER:
      return /^\d{4}-Q[1-4]$/.test(valueDate)
    case KpiPeriodType.WEEK:
      return /^\d{4}-W\d{1,2}$/.test(valueDate)
    case KpiPeriodType.ALLTIME:
      return valueDate === 'ALLTIME'
    default:
      return true
  }
}
